import React, { useState, useEffect } from "react";
import { z } from "zod";
import { Formik, Form } from "formik";

import { DatePicker, ErrorMessage, PrimaryButton, TextField } from "widgets";
import EditIcon from "@mui/icons-material/Edit";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useEditPackage } from "store/backend/apiHooks";
import Loader from "shared/Loader/Loader";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { CURRENCY_ID } from "constants/constants";

import styles from "./EditPackageDetails.module.scss";

const validationSchema = z.object({
  listedPrice: z
    .string()
    .min(1, "Price is required")
    .refine((val) => val >= 0, "Price must be positive"),
  discountValue: z
    .string()
    .min(1, "Discount is required")
    .refine((val) => val >= 0, "Discount must be positive"),
  sellingPrice: z
    .string()
    .min(1, "Price is required")
    .refine((val) => val >= 0, "Price must be positive"),
});

const handleOnClickSaveChanges = (
  values,
  editPackage,
  setIsEditable,
  openSnackbar,
  packageId
) => {

  const payload = {
    start_selling_date: values.startDate,
    end_selling_date: values.endDate,
    listed_price: values.listedPrice,
    discount_value: values.discountValue,
    selling_price: values.sellingPrice,
    is_percentage: values.isPercentage,
    currency_id: CURRENCY_ID,
  };

  editPackage(
    { packageId, payload },
    {
      onSuccess: (data) => {
        openSnackbar("Package details updated successfully!", "success");
        setIsEditable(false);
      },
      onError: (error) => {
        openSnackbar(
          error.message || "Failed to update package details.",
          "error"
        );
      },
    }
  );
};

const handleToggleEdit = (setIsEditable) => {
  setIsEditable((prev) => !prev);
};
const recalculateSellingPrice = (listedPrice, discountValue) => {
  const price = parseFloat(listedPrice) || 0;

  if (discountValue.includes("%")) {
    const percentage = parseFloat(discountValue.replace("%", "")) || 0;
    const discountAmount = (price * percentage) / 100;
    return (price - discountAmount).toFixed(2);
  } else {
    const fixedDiscount = parseFloat(discountValue) || 0;
    return (price - fixedDiscount).toFixed(2);
  }
};
const EditPackageDetails = ({ activePackage }) => {
  const [isEditable, setIsEditable] = useState(false);
  const { openSnackbar } = useSnackbarContext();
  const packageId = activePackage.package_id;
  const { mutate: editPackage, isPending: editPackageLoading } =
    useEditPackage();

  return (
    <Formik
      initialValues={{
        listedPrice: activePackage.price?.listed_price,
        discountValue: activePackage.price?.discount_value,
        isPercentage: activePackage.price?.is_percentage,
        sellingPrice: activePackage.price?.selling_price,
        startDate: activePackage?.start_selling_date,
        endDate: activePackage?.end_selling_date,
      }}
      validationSchema={toFormikValidationSchema(validationSchema)}
      enableReinitialize
      onSubmit={(values) =>
        handleOnClickSaveChanges(
          values,
          editPackage,
          setIsEditable,
          openSnackbar,
          packageId
        )
      }
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form className={styles.editableFields}>
          <span
            className={styles.editIcon}
            title={isEditable ? "Save Changes" : "Edit Details"}
          >
            {isEditable ? (
              <PrimaryButton
                onClick={() =>
                  handleOnClickSaveChanges(
                    values,
                    editPackage,
                    setIsEditable,
                    openSnackbar,
                    packageId
                  )
                }
              >
                {editPackageLoading ? <Loader /> : "Save Changes"}
              </PrimaryButton>
            ) : (
              <span
                className={styles.editIcon}
                onClick={() => handleToggleEdit(setIsEditable)}
              >
                <h3>Edit Package Details</h3>
                <EditIcon style={{ color: "blue", cursor: "pointer" }} />
              </span>
            )}
          </span>
          <span className={styles.dashedLine}></span>
          <div className={styles.defaultValues}>
            <div className={styles.displayDetails}>
              <span className={styles.title}>Start Selling Date</span>
              <DatePicker
                value={values.startDate}
                onChange={(date) => setFieldValue("startDate", date)}
                disabled={!isEditable}
                minDate={new Date()}
              />
              {errors.startDate && (
                <ErrorMessage>{errors.startDate}</ErrorMessage>
              )}
            </div>
            <div className={styles.displayDetails}>
              <span className={styles.title}>End Selling Date</span>
              <DatePicker
                value={values.endDate}
                onChange={(date) => setFieldValue("endDate", date)}
                disabled={!isEditable}
                minDate={new Date()}
              />
              {errors.endDate && <ErrorMessage>{errors.endDate}</ErrorMessage>}
            </div>
          </div>
          <div className={styles.defaultValues}>
            <div className={styles.displayDetails}>
              <span className={styles.title}>Default Price</span>
              <TextField
                value={values.listedPrice}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFieldValue("listedPrice", newValue);
                  const sellingPrice = recalculateSellingPrice(
                    newValue,
                    values.discountValue
                  );
                  setFieldValue("sellingPrice", sellingPrice);
                }}
                disabled={!isEditable}
              />
              {errors.listedPrice && (
                <ErrorMessage>{errors.listedPrice}</ErrorMessage>
              )}
            </div>

            <div className={styles.displayDetails}>
              <span className={styles.title}>Default Discount</span>
              <TextField
                value={values.discountValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFieldValue("discountValue", newValue);
                  const sellingPrice = recalculateSellingPrice(
                    values.listedPrice,
                    newValue
                  );
                  setFieldValue("sellingPrice", sellingPrice);
                }}
                disabled={!isEditable}
              />
              {errors.discountValue && (
                <ErrorMessage>{errors.discountValue}</ErrorMessage>
              )}
            </div>
            <div className={styles.displayDetails}>
              <span className={styles.title}>Selling Price</span>
              <TextField value={values.sellingPrice} disabled={true} />
              {errors.sellingPrice && (
                <ErrorMessage>{errors.sellingPrice}</ErrorMessage>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default EditPackageDetails;
